import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CloseOutlined } from '@mui/icons-material';
import {
  Alert,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';

import TextInput from '../TextInput';

import FilterByPopover, {
  EMPTY_FILTERS,
  StaffingFilters,
} from './FilterByPopover';
import StaffingTabsV2, {
  StaffingTabV2Value,
  resolveWorkerTab,
} from './StaffingTabsV2';
import useStaffingActions from './useStaffingActions';
import WorkerRowV2 from './WorkerRowV2';

import ShiftsList from '@/components/WorkerStaffingModel/ShiftsList';

import ConfirmationModal from '@/components/ConfirmationModal';
import CreatePoolModal from '@/components/PreferredWorkers/modals/CreatePoolModal';
import PoolDepthCard from '@/components/PreferredWorkers/PoolDepthCard';
import Stack from '@/components/Stack';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { GAEvent } from '@/constants/gaevents';
import { WORKERS_LISTING_LIMIT } from '@/constants/general';
import { ApiEndpoints } from '@/constants/urls';
import Text from '@/elements/Text';
import useAuth from '@/hooks/useAuth';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useMediaQuery from '@/hooks/useMediaQuery';
import { PoolRow, PreferredPoolsAPI } from '@/services/preferredPoolsAPI';
import { JobTypeEnum, JobWorkerStatusEnum } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { WorkerTabs } from '@/util/axios/types';
import {
  trackDependabilityFilterApplied,
  trackStaffingModalOpened,
  trackStaffingTabChanged,
} from '@/util/vexo';
import { IconColor } from '@/styles/colors';
import { workerName } from '@/util/worker';
import { useFeatureValue } from '@growthbook/growthbook-react';

// Fork of WorkerStaffingModel (see that file's 804 lines for the surface
// this is forked from) that assembles F11 (useStaffingActions) + F12
// (WorkerRowV2) + F13 (StaffingTabsV2) behind the new Pool/Favorites/Browse
// tab bar, with its own worker-list fetch. WorkerStaffingModel,
// StaffingTabs, StaffingWorkerProfileRow, and StaffingUtils stay
// byte-for-byte unchanged — duplication here is the deliberate approved
// choice (see F11-F13 briefs). ShiftsList is reused as-is (not forked): it
// has no Pool/Favorites/Browse-specific behavior to diverge on.
type Props = {
  hideModal: (refetch?: boolean) => void;
  selectedJobs: Record<string, any>[];
  customerId: string;
  jobTaxType: string;
  showShiftList?: boolean;
  timezone?: string;
  qualityGateActive?: boolean;
  // Tier data doesn't exist until GRAVY-10231 merges; the Filter-by
  // popover's Dependability section (and the graphy `tiers` param, which is
  // guarded the same way as tier_by_worker_id) only becomes meaningful then.
  // F15 decides when this flips on; F14 must not default it true.
  showTier?: boolean;
};

const PreferredStaffingModal = ({
  selectedJobs,
  hideModal,
  customerId,
  jobTaxType,
  showShiftList = true,
  timezone,
  qualityGateActive = false,
  showTier = false,
}: Props) => {
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const phoneStyles = phoneOnly
    ? { height: '90vh', width: '100%', borderRadius: '20px' }
    : {};

  // LTA staffing has no pool/favorites concept — the original suppresses the
  // tab bar entirely for LTA and fetches tab=all (WorkerStaffingModel
  // index.tsx:646). Mirror that: open on Browse/All and render no tabs.
  // GRAVY-10396 C1/C4: for gig jobs 'preferred_pool' is only a provisional
  // default — the mount effect below resolves the real one (pool with
  // members → pool tab; else favorites if any exist; else Browse) and no
  // list fetch fires until it has (tabResolved), so an empty pool never
  // flashes an empty pool tab.
  const [tabV2, setTabV2] = useState<StaffingTabV2Value>(
    selectedJobs[0]?.jobType === JobTypeEnum.LTA ? 'browse' : 'preferred_pool',
  );
  const [tabResolved, setTabResolved] = useState(
    selectedJobs[0]?.jobType === JobTypeEnum.LTA,
  );
  // A tab the admin picked while the default was still resolving must win
  // over the resolved default.
  const userPickedTab = useRef(false);
  // Delta C: within-tab refinements from the Filter-by popover, sent as
  // composable params alongside `tab` (never as a tab value). Reset on tab
  // switch, matching the prototype's switchModalTab → resetFilters().
  const [filters, setFilters] = useState<StaffingFilters>(EMPTY_FILTERS);
  const [workersList, setWorkersList] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const debSearch = useDebouncedValue(search, 400);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // A failed fetch must not leave the previous tab's rows rendered under the
  // new tab's label — the list is reset and this drives a retry affordance.
  const [fetchError, setFetchError] = useState(false);
  const {
    currentAdminIsCustomerAdmin: isClientAdmin,
    currentAdminIsTenantAdmin,
  } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const { logEvent } = useAnalytics();
  // GRAVY-10265 fix-wave FIX B: the fork's fetch dropped the badgeIds
  // search param WorkerStaffingModel/index.tsx:161-176 sends when this
  // toggle is on — without it, a certificate-gated job (TIPS, food handler)
  // shows the full unnarrowed list, letting an admin hire an uncertified
  // worker onto a compliance-gated shift. Re-derived here, not consumed
  // elsewhere in this fork.
  const workerBadge = useFeatureValue(FEATURE_TOGGLE.WorkerBadge, false);

  // Delta D (2026-07-30 prototype alignment, prototype .pool-summary): the
  // job-skill's active pool backs the read-only depth card at the top of the
  // modal. Server-driven gate parity with PreferredWorkers: listPools maps
  // 404/403 to enabled:false, so client admins and flag-off environments
  // simply render no card.
  const [pool, setPool] = useState<PoolRow | null>(null);
  // GRAVY-10396 C3: the no-pool empty state links into the existing
  // pool-creation flow (CreatePoolModal), gated on the pools API being
  // enabled for this admin. poolSkillIds feeds the modal's already-pooled
  // skill filter.
  const [poolsEnabled, setPoolsEnabled] = useState(false);
  const [poolSkillIds, setPoolSkillIds] = useState<number[]>([]);
  const [showCreatePool, setShowCreatePool] = useState(false);
  // GRAVY-10943 FP-1: once pools follow Custom Rates the server refuses a
  // hand-made pool (CreateService), so the set-up link would only 422.
  const [followsRates, setFollowsRates] = useState(false);

  const staffing = useRef(false);
  // Monotonic fetch sequence: any state-changing response must still be the
  // LATEST request. Without it a slow response from a previous tab/search
  // landing late overwrote (or, page>1, appended into) the current tab's
  // list — on the Pool tab that presents non-pool workers as pool members.
  const fetchSeq = useRef(0);
  const [jobs, setJobs] = useState(selectedJobs);

  const jobInfo = jobs[0];
  const isLTA = jobInfo.jobType === JobTypeEnum.LTA;
  const selectedIds = jobs
    .filter((j) => j.hiredWorkersCount < j.quantity)
    .map((j) => j.id);

  const backgroundCheckRequired = jobs.some(
    (job) => job.requireBackgroundChecks,
  );

  const resolvedTab: WorkerTabs = resolveWorkerTab(tabV2);

  useEffect(() => {
    // GRAVY-10396: no list fetch until the default tab is resolved (or the
    // admin picks a tab themselves, which resolves it immediately).
    if (!tabResolved) return;
    if (debSearch) {
      logEvent(GAEvent.WorkerListSearch);
    }
    setPage(1);
    fetchWorkers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabV2, filters, debSearch, tabResolved]);

  useEffect(() => {
    // LTA staffing has no pool concept (matching the tab-bar suppression
    // above) — skip the fetch entirely.
    if (isLTA) return undefined;
    let cancelled = false;
    (async () => {
      // GRAVY-10396 C1/C4 (Product decision 59948 §C): pool tab only when
      // the job-skill's pool has 1+ members; otherwise Favorites if the
      // client has any, else Browse.
      let defaultTab: StaffingTabV2Value = 'browse';
      // Hoisted out of the try block (was scoped to it) so the
      // staffing_modal_opened tracker below can read the matched pool —
      // 2026-09-02 analytics plan.
      let match: PoolRow | undefined;
      try {
        const result = await PreferredPoolsAPI.listPools(customerId);
        if (cancelled) return;
        if (result.enabled) {
          setPoolsEnabled(true);
          setFollowsRates(Boolean(result.seeding?.follows_custom_rates));
          setPoolSkillIds(
            result.pools
              .filter((p) => p.status === 'active')
              .map((p) => Number(p.skill.id)),
          );
          match = result.pools.find(
            (p) =>
              p.status === 'active' &&
              Number(p.skill.id) === Number(jobInfo.skill?.id),
          );
          if (match) {
            setPool(match);
            if (match.active_member_count > 0) defaultTab = 'preferred_pool';
          }
        }
      } catch {
        // The summary card is informational; staffing works without it —
        // the default falls through to Favorites/Browse below.
      }
      if (cancelled || userPickedTab.current) return;
      if (defaultTab !== 'preferred_pool') {
        // C4 favorites fallback: a 1-row probe decides Favorites vs Browse.
        try {
          const probe = await axiosRubyClient.get(
            ApiEndpoints.GET_WORKERS_LIST,
            {
              params: {
                skillId: Number(jobInfo.skill?.id),
                customerId: Number(customerId),
                addressId: Number(jobInfo.address?.id),
                jobId: jobInfo.id,
                page: 1,
                perPage: 1,
                search: '',
                tab: resolveWorkerTab('favourites'),
                workerType: 'gig',
                taxType: jobTaxType || 'all',
                backgroundCheckRequired: backgroundCheckRequired,
              },
            },
          );
          if ((probe?.data?.data || []).length > 0) defaultTab = 'favourites';
        } catch {
          // Probe is best-effort; Browse is always a safe landing.
        }
      }
      if (cancelled || userPickedTab.current) return;
      setTabV2(defaultTab);
      setTabResolved(true);
      trackStaffingModalOpened({
        job_id: jobInfo.id,
        resolved_tab: defaultTab,
        tab_source: 'default',
        pool_id: match?.id ?? null,
        pool_member_count: match?.active_member_count ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      let isThrottled = false;

      const handleScroll = () => {
        if (isThrottled) return;

        const scrollTop = container.scrollTop;
        const clientHeight = container.clientHeight;
        const scrollHeight = container.scrollHeight;
        const offset = 200;
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - offset;

        if (isNearBottom && hasMore && !loading) {
          isThrottled = true;
          fetchWorkers(page + 1);
          setPage((prev) => prev + 1);

          setTimeout(() => {
            isThrottled = false;
          }, 300);
        }
      };

      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, debSearch, page]);

  // Mirrors WorkerStaffingModel's fetchWorkers param set, including the
  // GrowthBook-gated badgeIds param (workerBadge, above) — minus the V3
  // GraphQL POST branch (the parent only ever mounts this fork when
  // JobFulfillment is already on, so the Ruby REST endpoint is the only
  // path here). `tab` is always the
  // WorkerTabs value resolved by StaffingTabsV2's resolveWorkerTab helper —
  // never the raw tabV2 string — so a typo or unmapped tab is a compile
  // error, not a silently-empty backend search. Popover refinements travel
  // as separate composable params, never as tab values.
  const fetchWorkers = async (fetchPage: number) => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    try {
      const res = await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_LIST, {
        params: {
          skillId: Number(jobInfo.skill?.id),
          customerId: Number(customerId),
          addressId: Number(jobInfo.address?.id),
          jobId: jobInfo.id,
          page: fetchPage,
          perPage: WORKERS_LISTING_LIMIT,
          search: debSearch,
          tab: resolvedTab,
          workerType: isLTA ? 'lta' : 'gig',
          taxType: jobTaxType || 'all',
          backgroundCheckRequired: backgroundCheckRequired,
          // Delta C refinements — composable server-side narrowing within the
          // active tab (graphy ScopeBuilderService.apply_refinement_filters).
          // null (not false) so axios omits inactive filters, like badgeIds.
          gravyTrained: filters.gravyTrained || null,
          pastWorkers: filters.pastWorkers || null,
          tiers: filters.tiers.length > 0 ? filters.tiers.join(',') : null,
          badgeIds:
            workerBadge && jobInfo.certificates?.length > 0
              ? jobInfo.certificates.map((c) => c.id).join(',')
              : null,
        },
      });
      // A newer fetch started while this one was in flight — its response
      // owns the list now; this one must not touch any state (including
      // loading, which the newer fetch controls).
      if (seq !== fetchSeq.current) return;

      setFetchError(false);
      const respData = res?.data?.data || [];

      if (fetchPage > 1) {
        setWorkersList((prev) => {
          const existingIds = new Set(prev.map((worker) => worker.id));
          const newWorkers = respData.filter(
            (worker) => !existingIds.has(worker.id),
          );
          return [...prev, ...newWorkers];
        });
      } else {
        setWorkersList(respData);
      }

      setHasMore(respData.length > 0);
    } catch (e) {
      console.error(e);
      // Only the LATEST request may touch state (same rule as the success
      // path). A failed first page clears the list so the previous tab's
      // workers are never presented under the new tab's label.
      if (seq === fetchSeq.current) {
        setFetchError(true);
        if (fetchPage === 1) setWorkersList([]);
        setHasMore(false);
      }
    }
    if (seq === fetchSeq.current) setLoading(false);
  };

  const extractLocationFromURL = (url: string) => {
    if (url.includes('/jobs/')) {
      return 'Job Details Page';
    } else if (url.includes('/orders/')) {
      return 'Order Details Page';
    }
    return 'Unknown';
  };

  // GA logging the F11 hook deliberately did not port (it needed this
  // search-tab UI state, which lives here, not in the hook — see F11
  // report). Fired from onHired/onOffered, the exact call sites
  // WorkerStaffingModel's logStaffingEvent used (right after
  // updateJobAfterHire/updateJobAfterOffer), so it fires once per
  // successful (job, worker) pair on both the initial staff() call and the
  // confirmRehire() retry loop.
  const logStaffingEvent = (action: 'hire' | 'invite', jobId: any) => {
    if (action === 'invite') {
      logEvent(GAEvent.JobInviteWorker, jobId, {
        inviteSentFrom: extractLocationFromURL(window.location.href),
        tab: resolvedTab,
      });
    } else {
      logEvent(GAEvent.JobHireWorker, jobId, {
        hiredFrom: extractLocationFromURL(window.location.href),
        tab: resolvedTab,
      });
    }
  };

  const buildLocalWorker = (workerId: any, workerData?: any) => ({
    __typename: 'Worker',
    id: String(workerId),
    avatarUrl: workerData?.avatarUrl ?? null,
    user: workerData?.user ?? null,
  });

  const updateJobAfterHire = (
    jobId: any,
    hiredWorkersCount: any,
    workerId: any,
    jobWorker: any,
    workerData?: any,
  ) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) => {
        if (job.id !== jobId) return job;
        return {
          ...job,
          hiredWorkersCount: hiredWorkersCount ?? job.hiredWorkersCount,
          jobWorkers: [
            ...job.jobWorkers,
            {
              __typename: 'JobWorker',
              id: String(jobWorker.id),
              status: jobWorker.status,
              showed: null,
              rejectedAt: null,
              worker: buildLocalWorker(workerId, workerData),
            },
          ],
          jobOffers: job.jobOffers.filter(
            (o) => o.worker.id !== String(workerId),
          ),
        };
      }),
    );
    logStaffingEvent('hire', jobId);
  };

  const updateJobAfterOffer = (
    jobId: any,
    workerId: any,
    jobOffer: any,
    workerData?: any,
  ) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) => {
        if (job.id !== jobId) return job;
        return {
          ...job,
          jobOffers: [
            ...job.jobOffers,
            {
              __typename: 'JobOffer',
              id: String(jobOffer.id),
              acceptedAt: null,
              rejectedAt: null,
              worker: buildLocalWorker(workerId, workerData),
            },
          ],
        };
      }),
    );
    logStaffingEvent('invite', jobId);
  };

  const {
    staff,
    inviteMany,
    isStaffing,
    errors,
    clearErrors,
    rehireState,
    confirmRehire,
    cancelRehire,
    conflictWarning,
    resolveConflict,
    overtimeModal,
  } = useStaffingActions({
    jobs,
    onHired: updateJobAfterHire,
    onOffered: updateJobAfterOffer,
    staffingTab: tabV2,
    pool,
  });

  // Ported from WorkerStaffingModel's `staffing.current = true;` at the end
  // of staffingHandler — marks "an action was attempted" (success or
  // failure) so closing the modal tells the parent to refetch, regardless
  // of whether this particular attempt panned out.
  const handleHire = (worker: Record<string, any>) => {
    staffing.current = true;
    staff(worker, 'hire');
  };
  const handleInvite = (worker: Record<string, any>) => {
    staffing.current = true;
    staff(worker, 'invite');
  };

  // Delta E (prototype #inviteAllBtn): invites every currently-listed
  // eligible worker. Conflicted workers are excluded up front (the
  // per-worker conflict dialog must not chain in a bulk action — the
  // prototype simply invites what's visible); rehire-confirm workers are
  // skipped inside the hook and surfaced in the errors alert.
  const handleInviteAll = () => {
    const eligible = workersList.filter(
      (w) =>
        w.invitable &&
        !w.hasConflicts &&
        !checkWorkerHiredOrInvited(w.id, 'invite') &&
        !checkWorkerHiredOrInvited(w.id, 'hire'),
    );
    if (eligible.length === 0) return;
    staffing.current = true;
    inviteMany(eligible);
  };

  const handleClose = () => hideModal(staffing.current);

  // Single tab-change path — used by the tab bar AND the empty-state links —
  // so search/filter reset, GA telemetry, and the picked-tab override behave
  // identically wherever a tab switch originates.
  const handleTabChange = (t: StaffingTabV2Value) => {
    userPickedTab.current = true;
    setSearch('');
    // Prototype switchModalTab: filters reset on every tab switch.
    setFilters(EMPTY_FILTERS);
    // Only a REAL switch is a tab change — clicking the already-active tab
    // still resets search/filters (prototype behaviour, unchanged above) but
    // must not emit a change event with from === to.
    if (t !== tabV2) {
      trackStaffingTabChanged({ job_id: jobInfo.id, from: tabV2, to: t });
    }
    // The default-tab mount effect (above) may still be resolving — a click
    // before it lands means THIS is the first resolution the modal gets, so
    // it ALSO counts as a staffing_modal_opened (tab_source: 'user').
    if (!tabResolved) {
      trackStaffingModalOpened({
        job_id: jobInfo.id,
        resolved_tab: t,
        tab_source: 'user',
        pool_id: pool?.id ?? null,
        pool_member_count: pool?.active_member_count ?? null,
      });
    }
    setTabV2(t);
    setTabResolved(true);
    // The original logs every tab select with the resolved WorkerTabs value
    // (StaffingTabs.tsx:149-152 via getTabValue) — kept so the experiment's
    // treatment arm has the same tab-engagement telemetry as control.
    // Popover refinements don't map onto WorkerTabs values, so they carry
    // no tab GA.
    logEvent(resolveWorkerTab(t));
  };

  const isPastSchedule = (schedule: Record<string, any>) =>
    new Date(schedule.firstShiftStartAt).getTime() < new Date().getTime();

  const allSelectedSchedulesArePast = () => jobs.every(isPastSchedule);

  const checkWorkerHiredOrInvited = (workerId: any, action = '') => {
    const checkProperty = action === 'hire' ? 'jobWorkers' : 'jobOffers';
    return jobs.every((job) =>
      job[checkProperty].some(
        (w) =>
          w.worker.id === workerId.toString() &&
          w.status !== JobWorkerStatusEnum.DISMISSED &&
          w.status !== JobWorkerStatusEnum.DROPPED &&
          !w.rejectedAt,
      ),
    );
  };

  // Mirrors StaffingWorkerProfileRow's buttonsDisableCheck (hire) vs
  // inviteButtonDisable (invite) exactly: hiring onto an all-past-schedule
  // selection is a deliberately preserved affordance (e.g. backfilling a
  // hire on a completed job) — WorkerStaffingModel/index.tsx's
  // hireButtonClick never gates on allSelectedSchedulesArePast(), only
  // inviteButtonClick does. WorkerRowV2 (F12) exposes hireDisabled and
  // inviteDisabled independently for exactly this asymmetry.
  const rowDisabled = selectedIds.length === 0 || isStaffing;
  const inviteRowDisabled = rowDisabled || allSelectedSchedulesArePast();
  // The original hands the row a COMPOSITE, not the raw flag
  // (WorkerStaffingModel index.tsx:714: `isLTA={isLTA ||
  // allSelectedSchedulesArePast()}`): on an all-past selection the row's
  // render-gating must behave LTA-like, so a client admin keeps a usable
  // Hire button (backfill) and a conflict-free Invite is hidden rather than
  // shown disabled. Passing raw isLTA left a client admin with zero usable
  // actions on any job whose first shift had started.
  const rowGatingLTA = isLTA || allSelectedSchedulesArePast();

  // GRAVY-10396 C2/C3 (Product decision 59948 §C): "No workers match your
  // search" is reserved for an actual zero-result search; otherwise the empty
  // copy is tab-specific, and the pool tab distinguishes no-pool (set-up
  // link into the existing CreatePoolModal) from empty-pool.
  const filtersActive =
    filters.gravyTrained || filters.pastWorkers || filters.tiers.length > 0;

  const browseLink = (
    <MuiButton
      size="small"
      variant="text"
      onClick={() => handleTabChange('browse')}
    >
      Browse all workers
    </MuiButton>
  );

  const renderEmptyState = () => {
    if (debSearch) return <>No workers match your search.</>;
    if (filtersActive) return <>No workers match your filters.</>;
    if (!isLTA && tabV2 === 'preferred_pool') {
      if (!pool && poolsEnabled && followsRates) {
        return (
          <>
            This client doesn&apos;t have a{' '}
            {jobInfo?.skill?.name || 'preferred worker'} pool yet. Add a Custom
            Rate for {jobInfo?.skill?.name || 'this skill'} to create its pool.{' '}
            {browseLink}
          </>
        );
      }
      if (!pool && poolsEnabled) {
        return (
          <>
            This client doesn&apos;t have a{' '}
            {jobInfo?.skill?.name || 'preferred worker'} pool yet.{' '}
            {!isClientAdmin && (
              <MuiButton
                size="small"
                variant="text"
                onClick={() => setShowCreatePool(true)}
              >
                Set up a preferred pool for this skill
              </MuiButton>
            )}
          </>
        );
      }
      if (pool && pool.active_member_count > 0) {
        // Members exist but none are staffable for this job (readiness /
        // eligibility scoping — GRAVY-10395 will surface them with reasons).
        return <>No pool members are available for this job. {browseLink}</>;
      }
      return <>No preferred workers in this pool yet. {browseLink}</>;
    }
    if (!isLTA && tabV2 === 'favourites') {
      return <>No favorite workers for this skill yet. {browseLink}</>;
    }
    return <>No workers available.</>;
  };

  return (
    <Dialog
      PaperProps={{
        id: 'preferred-staffing-modal',
        'data-testid': 'preferred-staffing-modal',
        // Prototype .staff-modal dimensions (2026-07-30 alignment): 800×760,
        // max-height 88vh, radius 8 — replacing the 1100px/92vh/20px carried
        // from the WorkerStaffingModel fork.
        style: {
          height: '760px',
          width: '800px',
          overflowX: 'hidden',
          borderRadius: '8px',
          maxHeight: '88vh',
          position: 'relative',
          ...phoneStyles,
        },
      }}
      fullScreen={phoneOnly}
      maxWidth={'lg'}
      open={true}
      sx={{
        '& .MuiDialog-container': phoneOnly
          ? { alignItems: 'end', marginBottom: '2px' }
          : {},
      }}
      onClose={handleClose}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            borderRadius: '8px',
          }}
        >
          <LinearProgress
            sx={{
              width: '200px',
              height: '4px',
            }}
          />
        </div>
      )}
      <DialogTitle style={{ padding: '10px 22px' }}>
        {/* Product feedback 2026-08-27: keep production's title
            "Staff Workers for {skill}" rather than the prototype's literal
            "Staff Job". */}
        <Text size={'lg'} weight={'semibold'}>
          {jobInfo?.skill?.name
            ? `Staff Workers for ${jobInfo.skill.name}`
            : 'Staff Job'}
        </Text>
        <IconButton
          aria-label="close"
          sx={{ float: 'right', padding: '2px', color: IconColor }}
          onClick={handleClose}
        >
          <CloseOutlined />
        </IconButton>
        {errors.length > 0 && (
          <Alert
            severity="warning"
            style={{ alignItems: 'center' }}
            onClose={() => clearErrors()}
          >
            <ul>
              {errors.map((e) => (
                <Fragment key={e.key}>
                  <li style={{ fontWeight: 'bold' }}>{e.title}</li>
                  <li>{e.msg}</li>
                </Fragment>
              ))}
            </ul>
          </Alert>
        )}
      </DialogTitle>
      <DialogContent
        ref={containerRef}
        id="preferred-worker-list"
        style={{ padding: '22px', paddingTop: '0px' }}
      >
        {showShiftList && (
          <ShiftsList
            isClientAdmin={isClientAdmin}
            isLTA={isLTA}
            jobs={jobs}
            phoneOnly={phoneOnly}
            selectedIds={selectedIds}
            timezone={timezone}
          />
        )}
        {isStaffing && <LinearProgress sx={{ height: '2px' }} />}
        <Divider sx={{ mt: '12px', mb: '8px' }} />
        {/* No onThresholdChange: the marker is display-only here — threshold
            editing stays on the Customer-page card (OQ-10). */}
        {pool && (
          <div style={{ marginBottom: '12px' }}>
            <PoolDepthCard pool={pool} />
          </div>
        )}
        {!isLTA && (
          <StaffingTabsV2
            skillName={jobInfo?.skill?.name}
            tab={tabV2}
            onTab={handleTabChange}
          />
        )}
        {/* Prototype .modal-toolbar: search + Filter-by popover side by side
            below the tabs. The popover is available on every tab (delta C);
            LTA keeps search only (no tabs, no pool/refinement concepts). */}
        <div
          style={{
            width: '100%',
            marginTop: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          {/* Prototype .modal-search-wrap: magnifier inside the field, on the
              left. TextInput has no adornment support, so the icon is
              absolutely positioned over the input's padding. */}
          <div style={{ flex: 1, position: 'relative' }}>
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 14,
                height: 14,
                color: '#979797',
                zIndex: 3,
                pointerEvents: 'none',
              }}
            />
            <TextInput
              css={{ paddingLeft: '34px', width: '100%' }}
              id="search-GravyWorkers"
              placeholder="Search GravyWorkers"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {!isLTA && (
            <FilterByPopover
              showGravyTrained={!!jobInfo?.skill?.gravyTrained}
              showTier={showTier}
              value={filters}
              onApply={(f) => {
                setFilters(f);
                if (f.tiers.length) {
                  trackDependabilityFilterApplied({
                    surface: 'staffing_modal',
                    vocabulary: 'v2',
                    tiers: f.tiers,
                  });
                }
              }}
            />
          )}
          {!isLTA && (
            // Prototype .invite-all-btn, verbatim styling. Disabled together
            // with the row Invite buttons (no open capacity / all-past
            // selection / a staffing call in flight).
            <button
              disabled={inviteRowDisabled || workersList.length === 0}
              style={{
                background:
                  inviteRowDisabled || workersList.length === 0
                    ? '#A1A1A1'
                    : '#44A735',
                border: 'none',
                borderRadius: '8px',
                height: '36px',
                width: '120px',
                boxShadow: '0 2px 2px rgba(0,0,0,0.12)',
                fontSize: '14px',
                fontWeight: 700,
                color: '#fff',
                cursor: 'pointer',
                flexShrink: 0,
              }}
              type="button"
              onClick={handleInviteAll}
            >
              Invite all
            </button>
          )}
        </div>
        {qualityGateActive && !isClientAdmin && (
          <div
            style={{
              backgroundColor: '#E7F4E680',
              border: '1px solid #A7DAA1',
              borderRadius: '4px',
              padding: '8px 16px',
              marginTop: '12px',
              fontFamily: 'Manrope',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '140%',
              color: '#2E8722',
              width: '100%',
            }}
          >
            Dependability gate active — only highly dependable workers are
            eligible for hire.
          </div>
        )}
        {/* No gap: rows carry the prototype's own 20px padding and the
            hairline between workers (.modal-user-row), so an extra flex gap
            would double the spacing and detach the separators. */}
        <div style={{ width: '100%', marginTop: '8px' }}>
          <Stack css={{ flexDirection: 'column', width: '100%' }} gap={0}>
            {workersList.map((w) => (
              <WorkerRowV2
                key={w.id}
                hireDisabled={rowDisabled}
                inviteDisabled={inviteRowDisabled}
                isClientAdmin={isClientAdmin}
                showPoolSource={currentAdminIsTenantAdmin}
                isHired={checkWorkerHiredOrInvited(w.id, 'hire')}
                isInvited={checkWorkerHiredOrInvited(w.id, 'invite')}
                isLTA={rowGatingLTA}
                skillName={jobInfo?.skill?.name}
                worker={w}
                onHire={handleHire}
                onInvite={handleInvite}
              />
            ))}
          </Stack>
          {!loading && fetchError && (
            <Text css={{ textAlign: 'center', marginTop: '24px' }}>
              We couldn&apos;t load workers.{' '}
              <MuiButton
                variant="text"
                size="small"
                onClick={() => {
                  setPage(1);
                  fetchWorkers(1);
                }}
              >
                Retry
              </MuiButton>
            </Text>
          )}
          {!loading &&
            !fetchError &&
            workersList.length === 0 &&
            tabResolved && (
              <Text css={{ textAlign: 'center', marginTop: '24px' }}>
                {renderEmptyState()}
              </Text>
            )}
          {loading && workersList.length > 0 && <LinearProgress />}
          {/* GRAVY-10438: the list pages on scroll; without an end marker a
              short last page reads as "scrolling loads nothing". */}
          {!loading && !fetchError && !hasMore && workersList.length > 0 && (
            <Text
              css={{ textAlign: 'center', marginTop: '16px', color: '#979797' }}
              data-testid="worker-list-end"
            >
              You&apos;ve reached the end of the list — all {workersList.length}{' '}
              matching workers are shown.
            </Text>
          )}
        </div>
      </DialogContent>

      {/* Conflict warning dialog when inviting an unavailable worker. The
          F11 hook only ever gates the invite path (mirroring
          WorkerStaffingModel's asymmetry — hire never conflict-checks). */}
      <Dialog open={!!conflictWarning} onClose={() => resolveConflict(false)}>
        <DialogTitle>Worker Has Schedule Conflicts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conflictWarning && (
              <>
                {`${workerName(conflictWarning.worker)} has existing shift${
                  (conflictWarning.worker.conflictDates?.length || 0) > 1
                    ? 's'
                    : ''
                } on ${conflictWarning.worker.conflictDates?.join(', ') || 'conflicting dates'}.`}
                {conflictWarning.worker.firstConflictJob && (
                  <>
                    <br />
                    {`Conflicting shift on ${conflictWarning.worker.firstConflictJob.date || ''}${conflictWarning.worker.firstConflictJob.startTime ? ` (${conflictWarning.worker.firstConflictJob.startTime} - ${conflictWarning.worker.firstConflictJob.endTime})` : ''}`}
                  </>
                )}
                <br />
                They will receive your invite but are currently booked for{' '}
                {(conflictWarning.worker.conflictDates?.length || 0) > 1
                  ? 'these dates'
                  : 'this date'}
                .
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => resolveConflict(false)}>Cancel</MuiButton>
          <MuiButton variant="contained" onClick={() => resolveConflict(true)}>
            Send Invite
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Rehire / already-offered confirm — fires once per submit when the
          loop collected any WORKER_ALREADY_DROPPED_JOB / ALREADY_OFFERED.
          Accept retries those jobs with force=true via the hook. */}
      {rehireState && (
        <ConfirmationModal
          acceptAction={confirmRehire}
          acceptButtonLabel={
            rehireState.action === 'hire' ? 'Hire worker' : 'Send Invite'
          }
          bodyContentText={
            rehireState.action === 'hire'
              ? `This worker has previously dropped the jobs on : ${rehireState.jobs
                  .map((j) => j.label || j.id)
                  .join(', ')}. Do you want to proceed?`
              : `This worker has previously declined an invitation for the jobs on : ${rehireState.jobs
                  .map((j) => j.label || j.id)
                  .join(', ')}. Do you want to proceed?`
          }
          denyAction={() => {}}
          denyButtonLabel="Cancel"
          hideModal={cancelRehire}
          title={`Confirmation`}
        />
      )}
      {/* GRAVY-10396 C3: the EXISTING pool-creation flow, opened from the
          no-pool empty state — never a second creation flow built here. Its
          Overlay stacks above the MUI Dialog ($overlay = 10000 > 1300). */}
      {showCreatePool && (
        <CreatePoolModal
          customerId={customerId}
          existingSkillIds={poolSkillIds}
          onClose={() => setShowCreatePool(false)}
          onCreated={(created) => {
            setPoolSkillIds((prev) => [...prev, Number(created.skill.id)]);
            if (Number(created.skill.id) === Number(jobInfo.skill?.id)) {
              setPool(created);
            }
          }}
        />
      )}
      {overtimeModal}
    </Dialog>
  );
};

export default PreferredStaffingModal;
